import axios from 'axios';
import ls, { get } from 'local-storage';

import { ICreateFile } from '../../../types/CreateFile';
import { IOrder } from '../../../types/Order';
import { IEscrowMember, ISettlementTeam } from '../../../types/Parties';
import { IUpdateSigner } from '../../../types/UpdateSigner';
import axiosInterceptor from '../../utils/axios';
import { compareOrderObjects } from '../../utils/compareOrderObjects';
import { mergeEvents, moveToStart } from '../../utils/helpers';
import { mergeOrderUpdates } from '../../utils/mergeOrderUpdates';
import { ConfigIProps } from './ordersTypes';
import {
  EnrichedResponse,
  UnlockSessionRequest,
  UnlockSessionResponse,
} from './viewOrdersAPI.types';

export const viewOrders = (
  query: string,
  paginationDirection: string | undefined,
  config: ConfigIProps,
  cancelToken: any
) => {
  return new Promise<any>((resolve, reject) => {
    const urlParams = new URLSearchParams(query);
    let url = config.baseURL + '/order-management/search?' + query;
    const ptsLocal: string = get('pts');
    let tokensArray = ptsLocal ? ptsLocal.split(',') : [];
    let page: number = urlParams.get('Page')
      ? Number(urlParams.get('Page'))
      : 0;

    // add pagination token
    switch (paginationDirection) {
      case 'prev':
        url += `&PaginationToken=${tokensArray[page - 1]}`;
        tokensArray.length = page;
        break;
      case 'next':
        url += '&PaginationToken=' + tokensArray[page - 1];
        break;
      case 'start':
        tokensArray = [];
        page = 0;
        break;
      default:
        if (page > 0) {
          url += '&PaginationToken=' + tokensArray[page - 1];
        }
        break;
    }

    const axiosConfig: any = {
      method: 'get',
      url: url,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      cancelToken: cancelToken,
    };

    axiosInterceptor(axiosConfig)
      .then((response) => {
        tokensArray[page] = response.data.PaginationToken;
        ls('pts', tokensArray.join());
        resolve({
          Orders: response.data.Orders,
          PaginationToken: response.data.PaginationToken,
        });
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          /// Request cancelled...
        } else {
          reject(
            error?.response?.data?.Message
              ? error.response.data.Message
              : error.message
          );
        }
      });
  });
};

export const viewOrder = (orderId: string, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/view-order?Id=' + orderId;
    axiosInterceptor
      .get(url)
      .then((response) => {
        const merged = mergeEvents(response.data);
        resolve(merged);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          /// Request cancelled...
        } else {
          reject(
            error?.response?.data?.Message
              ? error.response.data.Message
              : error.message
          );
        }
      });
  });
};

export const reloadOrder = (
  orderId: string,
  config: ConfigIProps,
  order: any
) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/view-order?Id=' + orderId;
    axiosInterceptor
      .get(url)
      .then((res) => {
        (async function () {
          const reloadedOrder = mergeEvents(res.data);
          const orderUpdates = await compareOrderObjects(order, reloadedOrder);
          resolve(orderUpdates);
        })();
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const cancelOrder = (orderId: string, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/cancel?OrderId=' + orderId;
    axiosInterceptor
      .get(url)
      .then((response) => {
        if (response.data.IsSuccess) {
          window.location.reload();
          resolve('');
        } else {
          reject({
            text: response.data.Message
              ? response.data.Message
              : 'Could not cancel order',
            type: 'error',
          });
        }
      })
      .catch((error) => {
        reject({
          text: error?.response?.data?.Message
            ? error.response.data.Message
            : error.message,
        });
      });
  });
};

export const unlockParty = (
  payload: Record<string, unknown>,
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/unlock-parties';
    axiosInterceptor
      .put(url, payload)
      .then(() => {
        resolve({});
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const unlockSession = (
  payload: UnlockSessionRequest,
  config: ConfigIProps
) => {
  return new Promise<
    EnrichedResponse & { activeParties: UnlockSessionResponse }
  >((resolve, reject) => {
    const url =
      config.baseURL + '/order-management/unlock-parties-signing-session';
    axiosInterceptor
      .post<UnlockSessionResponse>(url, payload)
      .then((resp) => {
        resolve({
          message: 'Session has been activated',
          activeParties: resp.data,
        });
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const submitOrder = (order: any, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/submit-order';
    const submitObj = {
      ...order,
      OrderDetails: {
        ...order.OrderDetails,
        CostCenter: order.OrderOverview.CostCenter,
        DocumentPackages: order.OrderOverview.DocumentPackages,
        NewLoans: order.OrderOverview.NewLoans,
        NotarizeBranches: order.OrderOverview.NotarizeBranches,
        Properties: order.OrderOverview.Properties,
      },
    };
    delete submitObj.OrderOverview;
    axiosInterceptor
      .post(url, submitObj)
      .then(() => {
        const redirect = '/orders/' + submitObj.Id;
        delete submitObj.CreatedAt;
        delete submitObj.CreatedBy;
        delete submitObj.ModifiedAt;
        delete submitObj.ModifiedBy;
        delete submitObj.Published;
        delete submitObj.TTL;
        resolve({
          message: 'Your order has been submitted.',
          redirect: redirect,
        });
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const createFAOrder = (
  createValues: ICreateFile,
  order: IOrder,
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    let url =
      config.baseURL +
      '/order-management/new-order-skeleton?FileNumber=' +
      createValues.fileNumber +
      '&IsExternal=false&OrderType=' +
      createValues?.OrderType;
    url =
      createValues.OrderType === 'RON'
        ? url + '&ProductType=' + createValues.NotarizeProductType
        : url;
    axiosInterceptor
      .get(url)
      .then((response) => {
        const errors = response?.data?.Errors;
        // If Fast has errors in File
        if (errors && errors.length > 0) {
          const formattedErrors = [];
          for (let i = 0; i < errors.length; i++) {
            formattedErrors.push(
              `${errors[i].ErrorMessage} ${errors[i].PropertyName}`
            );
          }
          reject(formattedErrors);
          return;
        }
        const orderSkeleton: IOrder = { ...response.data.Order };
        const signers = orderSkeleton?.OrderOverview?.Signers
          ? [...orderSkeleton.OrderOverview.Signers]
          : [];
        const escrowTeam = orderSkeleton?.OrderOverview?.EscrowTeam
          ? [...orderSkeleton.OrderOverview.EscrowTeam]
          : [];

        for (let i = 0; i < signers.length; i++) {
          signers[i].PhoneNumbers = moveToStart(
            signers[i].PhoneNumbers,
            'Type',
            'Cellular'
          );
        }
        if (orderSkeleton.OrderOverview?.Signers) {
          orderSkeleton.OrderOverview.Signers = signers;
        }

        for (let i = 0; i < escrowTeam.length; i++) {
          escrowTeam[i].PhoneNumbers = moveToStart(
            escrowTeam[i].PhoneNumbers,
            'Type',
            'Cellular'
          );
        }
        if (orderSkeleton.OrderOverview?.EscrowTeam) {
          orderSkeleton.OrderOverview.EscrowTeam = escrowTeam;
        }
        // IF RON ORDERS
        if (
          createValues.OrderType === 'RON' &&
          orderSkeleton.OrderOverview?.EscrowTeam
        ) {
          const addDirectRon: IEscrowMember = {
            Id: 'directron',
            FirstName: 'Direct',
            LastName: 'Ron',
            Type: 'Escrow Officer',
            PhoneNumbers: [
              {
                CountryCode: '1',
                AreaCode: '866',
                Number: '7204490',
                Extension: '',
                Type: 'Business Phone',
              },
            ],
            Emails: ['directron@firstam.com'],
          };
          orderSkeleton.OrderOverview.EscrowTeam.push(addDirectRon);
        }
        resolve(orderSkeleton);
      })
      .catch((error) => {
        const isArray = Array.isArray(error?.response?.data?.FaultedMembers);
        const noOffice = isArray
          ? error?.response?.data?.FaultedMembers[0]
          : error?.response?.data?.FaultedMembers;
        if (noOffice && noOffice === 'OwningOffice') {
          reject(
            'Escrow Service is required to open this order. Please correct your source file.'
          );
          return;
        }
        reject(
          error?.response?.data?.Message
            ? [error.response.data.Message]
            : [error.message]
        );
      });
  });
};

export const reloadFastData = (
  createValues: ICreateFile,
  order: IOrder,
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    let url =
      config.baseURL +
      '/order-management/new-order-skeleton?FileNumber=' +
      createValues.fileNumber +
      '&RegionId=' +
      createValues.region +
      '&IsExternal=false&OrderType=' +
      createValues.OrderType;
    url =
      createValues.OrderType === 'RON'
        ? url + '&ProductType=' + createValues.NotarizeProductType
        : url;
    axiosInterceptor
      .get(url)
      .then((response) => {
        const errors = response?.data?.Errors;
        // If Fast has errors in File
        if (errors && errors.length > 0) {
          const formattedErrors = [];
          for (let i = 0; i < errors.length; i++) {
            formattedErrors.push(
              `${errors[i].ErrorMessage} ${errors[i].PropertyName}`
            );
          }
          reject('Error Reloading Fast Data');
          return;
        }
        const orderSkeleton: IOrder = { ...response.data.Order };
        const currentOrderLoaded: IOrder = order;
        mergeOrderUpdates(orderSkeleton, currentOrderLoaded)
          .then((mergedData: any) => {
            resolve(mergedData);
          })
          .catch((err) => {
            console.error(err);
          });
      })
      .catch((error) => {
        const isArray = Array.isArray(error?.response?.data?.FaultedMembers);
        const noOffice = isArray
          ? error?.response?.data?.FaultedMembers[0]
          : error?.response?.data?.FaultedMembers;
        if (noOffice && noOffice === 'OwningOffice') {
          reject('Error Reloading Fast Data');
          return;
        }
        reject('Error Reloading Fast Data');
      });
  });
};

export const saveOrder = (order: any, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/save-order';
    const submitObj = {
      ...order,
    };
    delete submitObj.CreatedAt;
    delete submitObj.CreatedBy;
    delete submitObj.ModifiedAt;
    delete submitObj.ModifiedBy;
    delete submitObj.Published;
    delete submitObj.TTL;

    axiosInterceptor
      .post(url, submitObj)
      .then((response) => {
        submitObj.VersionNumber = response?.data?.VersionNumber;
        resolve({ message: 'Your order has been saved.', order: submitObj });
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const fetchNotaries = (orgId: string, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url =
      config.baseURL + `/order-management/list-ron-notaries?OrgId=${orgId}`;
    axiosInterceptor
      .get(url)
      .then((response) => {
        resolve(response.data.Notaries);
      })
      .catch(() => {
        reject(
          'There was a problem fetching notaries. Please try again later.'
        );
      });
  });
};

export const changeNotary = (
  data: {
    OrderId?: string;
    NotaryEmail?: string;
  },
  config: ConfigIProps
) => {
  return new Promise<{ order: IOrder; message?: string }>((resolve, reject) => {
    const url = config.baseURL + '/order-management/reassign-notary';

    axiosInterceptor
      .put(url, data)
      .then((response) => {
        resolve({ message: 'Notary Reassigned.', order: response.data.Order });
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const updateOrderActions = (
  data: {
    OrderId: string;
    ChecklistId: string;
    Complete: boolean;
  },
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/action';
    axiosInterceptor
      .put(url, data)
      .then(() => {
        resolve({});
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const updateSignerInformation = (
  data: {
    OrderId: string;
    SignerInformation: IUpdateSigner;
  },
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/order-management/update-signer-information';
    axiosInterceptor
      .put(url, data)
      .then(() => {
        resolve({ message: 'Successfully updated information' });
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const addSettlementAgentTeamMembers = (
  data: {
    OrderId: string;
    TeamMembersListToAdd: ISettlementTeam[];
  },
  config: ConfigIProps
) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + '/admin/add-settlement-agents';
    axiosInterceptor
      .post(url, data)
      .then(() => {
        resolve({ message: 'Successfully add settlement agent team members' });
      })
      .catch((error) => {
        reject(
          error?.response?.data?.Message
            ? error.response.data.Message
            : error.message
        );
      });
  });
};

export const fetchSettlementAgent = (email: string, config: ConfigIProps) => {
  return new Promise<any>((resolve, reject) => {
    const url = config.baseURL + `/settlementagents?Email=${email}`;
    axiosInterceptor
      .get(url)
      .then((response) => {
        resolve(response.data);
      })
      .catch(() => {
        reject(
          'There was a problem fetching Settlement Agent. Please try again later.'
        );
      });
  });
};
